/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

export const getSquareSideLength = (squaresCount: number, width: number, height: number) => {
    // Test for invalid input: check if amount of pixels in 'width * height' area is less than
    // the squares we need to place on this area
    if (width * height < squaresCount) {
        return 0;
    }

    // Initial guess.
    const aspect = height / width;
    const xf = Math.sqrt(squaresCount / aspect);
    const yf = xf * aspect;
    let x = Math.max(1.0, Math.floor(xf));
    let y = Math.max(1.0, Math.floor(yf));
    let xSize = Math.floor(width / x);
    let ySize = Math.floor(height / y);
    let squareSideLength = Math.min(xSize, ySize);

    // Test our guess:
    x = Math.floor(width / squareSideLength);
    y = Math.floor(height / squareSideLength);
    // We guessed too high.
    if (x * y < squaresCount) {
        if (((x + 1) * y < squaresCount) && (x * (y + 1) < squaresCount)) {
            // Case 2: the upper bound is correct compute the squareSideLength that will result in (x+1)*(y+1) tiles.
            xSize = Math.floor(width / (x + 1));
            ySize = Math.floor(height / (y + 1));

            squareSideLength = Math.min(xSize, ySize);
        } else {
            // Case 3: solve an equation to determine the final x and y dimensions and then compute
            // the squareSideLength that results in those dimensions.
            const testX = Math.ceil(squaresCount / y);
            const testY = Math.ceil(squaresCount / x);
            xSize = Math.min(Math.floor(width / testX), Math.floor(height / y));
            ySize = Math.min(Math.floor(width / x), Math.floor(height / testY));

            squareSideLength = Math.max(xSize, ySize);
        }
    }

    return squareSideLength;
};

const computePiePositionAndSize = (pieNumber: number, squareSideLength: number, width: number, height: number, titleHeight: number, totalPiesNumber: number) => {
    const paddingTop = 10;
    const globalPaddingTop = 5;

    const piesInRow = Math.min(Math.floor(width / squareSideLength), totalPiesNumber);
    const piesInColumn = Math.min(Math.floor(height / squareSideLength), totalPiesNumber);
    const widthGap = (width - squareSideLength * piesInRow) / piesInRow;
    const heightGap = (height - squareSideLength * piesInColumn) / piesInColumn;

    const xAxisPositionNumber = pieNumber % piesInRow === 0 ? piesInRow : pieNumber % piesInRow;
    const yAxisPositionNumber = Math.floor(pieNumber / piesInRow) + (pieNumber % piesInRow === 0 ? 0 : 1);

    const xAxisPosition = squareSideLength * xAxisPositionNumber - squareSideLength / 2 + (widthGap * xAxisPositionNumber) / 2;
    const yAxisPosition = squareSideLength * yAxisPositionNumber - squareSideLength / 2 + (heightGap * yAxisPositionNumber) / 2;

    const additionalSpaceCutFromPieSize = titleHeight + paddingTop;

    return {
        xAxisPositionPercent: (100 * (xAxisPosition - additionalSpaceCutFromPieSize / 2)) / width,
        yAxisPositionPercent: (100 * (yAxisPosition + additionalSpaceCutFromPieSize / 2 + globalPaddingTop)) / height,
        pieSizePercent: (100 * (squareSideLength - additionalSpaceCutFromPieSize)) / (width < height ? width : height)
    };
};

export const computePiePositionsAndSizes = (series: any[], containerWidth: number, containerHeight: number): { center: any[], size: string }[] => {

    const titleTextLineHeight = 15;
    const textTopBottomPadding = 10;
    // this is a default value which might be increased
    let titleHeight = titleTextLineHeight + textTopBottomPadding;
    for (let i = 0; i < series.length; i += 1) {
        const serie = series[i];
        if (serie.title && typeof serie.title.y !== 'undefined') {
            titleHeight = Math.max(titleHeight, Math.abs(serie.title.y));
        }
    }

    const piesNumber = series.length;
    const squareSideLength = getSquareSideLength(piesNumber, containerWidth, containerHeight);
    const pieSizeCenter: { center: any[], size: string }[] = [];

    for (let i = 0; i < piesNumber; i += 1) {
        const positionAndSize = computePiePositionAndSize(i + 1, squareSideLength, containerWidth, containerHeight, titleHeight, piesNumber);
        const centerArray = [];
        const xAxis = `${positionAndSize.xAxisPositionPercent}%`;
        const yAxis = `${positionAndSize.yAxisPositionPercent}%`;
        centerArray.push(xAxis);
        centerArray.push(yAxis);

        const size = `${positionAndSize.pieSizePercent}%`;
        pieSizeCenter.push({ center: centerArray, size });
    }

    return pieSizeCenter;
};

const exploreTotal = (searchedRowIndex: number, currentTotal: any): string | undefined => {
    if (currentTotal && currentTotal !== null) {
        if (currentTotal.index === searchedRowIndex) {
            return currentTotal.label;
        }
        return exploreTotal(searchedRowIndex, currentTotal.total);
    }
    return undefined;
}

export const getPieSliceName = (searchedRowIndex: number, currentRow: any): string[] | undefined => {
    if (currentRow.index === searchedRowIndex) {
        return [currentRow.label];
    }
    let partialResult;
    for (let i = 0; i < currentRow.children.length && partialResult === undefined; i += 1) {
        const subRow = currentRow.children[i];
        partialResult = getPieSliceName(searchedRowIndex, subRow);
    }
    if (partialResult) {
        if (currentRow.label && currentRow.label.length > 0) {
            partialResult.push(currentRow.label);
        }
        return partialResult;
    }
    const totalLabel = exploreTotal(searchedRowIndex, currentRow.total);
    if (totalLabel) {
        if (currentRow.label && currentRow.label.length > 0) {
            // if the total was found in this row and this row has a label return the label of the row
            return [currentRow.label];
        }
        return [totalLabel];
    }
    return undefined;
}

export const getPiePath = (searchedIndex: number, currentAxis: any): string[] | undefined => {
    if (currentAxis !== undefined && currentAxis !== null) {
        if (currentAxis.index === searchedIndex) {
            return [currentAxis.label];
        }
        let partialResult;
        for (let i = 0; i < currentAxis.children.length && partialResult === undefined; i += 1) {
            const subRow = currentAxis.children[i];
            partialResult = getPiePath(searchedIndex, subRow);
        }
        if (partialResult) {
            if (currentAxis.label && currentAxis.label.length > 0) {
                partialResult.unshift(currentAxis.label);
            }
            return partialResult;
        }
        if (currentAxis.total) {
            // was not found in the children, check in the total
            const currentAxisTotal = currentAxis.total;
            if (currentAxisTotal.index === searchedIndex) {
                // dont use the label of the total in the path
                return [];
            }
            // search in the total children
            if (currentAxisTotal.children) {
                for (let i = 0; i < currentAxisTotal.children.length && partialResult === undefined; i += 1) {
                    const subTotal = currentAxisTotal.children[i];
                    partialResult = getPiePath(searchedIndex, subTotal);
                }
                if (partialResult) {
                    if (currentAxis.label && currentAxis.label.length > 0) {
                        partialResult.unshift(currentAxis.label);
                    }
                    return partialResult;
                }
            }
            // search in the total total
            return getPiePath(searchedIndex, currentAxisTotal.total);
        }
    }
    return undefined;
}
